import React, {useEffect, useState} from "react";
import {
  Container,
  Divider,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Checkbox,
  Alert,
  FormControlLabel
} from "@mui/material";
import MDBox from "components/MDBox";
import MDInput from "components/MDInput";
import MDButton from "components/MDButton";
import {PATCH, GET} from "APIclient";
import Snackbar from "@mui/material/Snackbar";
import FormHelperText from "@mui/material/FormHelperText";
import {useLocation} from "react-router-dom";
import properties from "constants/ApplicationConstants";

function EmployeeAddress() {
  const [user, setUser] = useState({});
  const location = useLocation();
  const empId = location.state || window.sessionStorage.getItem("empId");
  const [hasAddressPermission, setHasAddressPermission] = useState(false);
  const [inputs, setInputs] = useState({
    empId: empId,
    presentAddressLine1: "",
    presentAddressLine2: "",
    presentAddressLine3: "",
    presentCountry: "",
    presentCity: "",
    presentState: "",
    presentPincode: "",
    permanentAddressLine1: "",
    permanentAddressLine2: "",
    permanentAddressLine3: "",
    permanentCountry: "",
    permanentCity: "",
    permanentState: "",
    permanentPincode: ""
  });

  const [isSameAddress, setIsSameAddress] = useState(false);
  const [errors, setErrors] = useState({});
  const [presentCountries, setPresentCountries] = useState([]);
  const [permanentCountries, setPermanentCountries] = useState([]);
  const [open, setOpen] = React.useState(false);
  const [snakColor, setSnakColor] = React.useState("");
  const [snakMessage, setSnakMessage] = React.useState("");
  const [isEdited, setIsEdited] = useState(false);

  const url1 = `employee/employee-details/${empId}`;

  useEffect(() => {
    const fetchCountries = async () => {
      try {
        const countryUrl = "admin/countries";
        const response = await GET(countryUrl);
        const data = response?.data?.data;
        if (data && response?.data?.statusCode === 200 && Array.isArray(data)) {
          setPresentCountries(data);
          setPermanentCountries(data);
        }
      } catch (error) {
        console.error("Error fetching countries:", error);
      }
    };

    fetchCountries();

    const fetchData = async () => {
      try {
        const response = await GET(url1);
        const data = response?.data?.data;
        if (data && response?.data?.statusCode === 200) {
          setUser(data.employee);
          const employeeData = data.employee;
          setInputs(prevInputs => ({
            ...prevInputs,
            ...employeeData,
            presentCountry: employeeData?.presentCountry || "",
            presentState: employeeData?.presentState || "",
            presentCity: employeeData?.presentCity || "",
            permanentCountry: employeeData?.permanentCountry || "",
            permanentState: employeeData?.permanentState || "",
            permanentCity: employeeData?.permanentCity || ""
          }));
          setHasAddressPermission(data.employee.permissions.includes("address"));
          if (employeeData?.presentCountry) {
            fetchPresentStatesAndCities(employeeData?.presentCountry, employeeData?.presentState);
          }
          if (employeeData?.permanentCountry) {
            fetchPermanentStatesAndCities(
              employeeData?.permanentCountry,
              employeeData?.permanentState
            );
          }
        }
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    };
    fetchData();
  }, []);

  useEffect(() => {
    if (isSameAddress) {
      setInputs(prevInputs => ({
        ...prevInputs,
        permanentAddressLine1: prevInputs.presentAddressLine1,
        permanentAddressLine2: prevInputs.presentAddressLine2,
        permanentAddressLine3: prevInputs.presentAddressLine3,
        permanentCountry: prevInputs.presentCountry,
        permanentState: prevInputs.presentState,
        permanentCity: prevInputs.presentCity,
        permanentPincode: prevInputs.presentPincode
      }));
    } else {
      setInputs(prevInputs => ({
        ...prevInputs,
        permanentAddressLine1: prevInputs.permanentAddressLine1 || "",
        permanentAddressLine2: prevInputs.permanentAddressLine2 || "",
        permanentAddressLine3: prevInputs.permanentAddressLine3 || "",
        permanentCountry: prevInputs.permanentCountry || "",
        permanentState: prevInputs.permanentState || "",
        permanentCity: prevInputs.permanentCity || "",
        permanentPincode: prevInputs.permanentPincode || ""
      }));
    }
    setPermanentStates(presentStates);
    setPermanentCities(presentCities);
  }, [
    isSameAddress,
    inputs.presentAddressLine1,
    inputs.presentAddressLine2,
    inputs.presentAddressLine3,
    inputs.presentCountry,
    inputs.presentCity,
    inputs.presentState,
    inputs.presentPincode
  ]);

  const handleChange = event => {
    if (!hasAddressPermission) return;

    const {name, value} = event.target;
    setInputs(prevInputs => ({
      ...prevInputs,
      [name]: value
    }));
    validateField(name, value);
    setIsEdited(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handleCheckboxChange = event => {
    if (!hasAddressPermission) return;

    setIsSameAddress(event.target.checked);
    setIsEdited(true);
  };

  const fieldLabels = {
    presentAddressLine1: "Present Address Line 1",
    presentAddressLine2: "Present Address Line 2",
    presentAddressLine3: "Present Address Line 3",
    presentState: "Present State",
    presentCity: "Present City",
    presentPincode: "Present Pincode",
    permanentAddressLine1: "Permanent Address Line 1",
    permanentAddressLine2: "Permanent Address Line 2",
    permanentAddressLine3: "Permanent Address Line 3",
    permanentState: "Permanent State",
    permanentCity: "Permanent City",
    permanentPincode: "Permanent Pincode"
  };

  const validateField = (name, value) => {
    let errorMessage = "";

    if (
      name === "presentAddressLine1" ||
      name === "presentAddressLine2" ||
      name === "permanentAddressLine1" ||
      name === "permanentAddressLine2"
    ) {
      if (value.trim() === "") {
        errorMessage = `${fieldLabels[name]} is required.`;
      }
    } else if (name === "presentCity" || name === "permanentCity") {
      if (value.trim() === "") {
        errorMessage = `${fieldLabels[name]} is required.`;
      }
    } else if (name === "presentState" || name === "permanentState") {
      if (value.trim() === "") {
        errorMessage = `${fieldLabels[name]} is required.`;
      }
    } else if (name === "presentPincode" || name === "permanentPincode") {
      const pincodeRegex = /^[0-9]{6}$/;
      if (value.trim() === "") {
        errorMessage = `${fieldLabels[name]} is required.`;
      } else if (!pincodeRegex.test(value)) {
        errorMessage = "Please enter a valid 6-digit pincode.";
      }
    }

    setErrors(prevErrors => ({
      ...prevErrors,
      [name]: errorMessage
    }));
  };

  const handleSubmit = async () => {
    if (!hasAddressPermission) return;

    const url = `employee/address`;
    try {
      const response = await PATCH(url, inputs);
      if (response?.data?.statusCode === 200) {
        setSnakColor("success");
        setSnakMessage("Address Update successful");
        setOpen(true);
        setIsEdited(false);
      }
    } catch (error) {
      setSnakMessage(error.response?.data?.message || "Failed to save employee address.");
      setSnakColor("error");
      setOpen(true);
    }
  };

  const [presentStates, setPresentStates] = useState([]);
  const [permanentStates, setPermanentStates] = useState([]);
  const [presentCities, setPresentCities] = useState([]);
  const [permanentCities, setPermanentCities] = useState([]);

  const fetchPresentStatesAndCities = async (country, presentState) => {
    try {
      const url = `admin/location-list/${country}`;
      const response = await GET(url);
      if (response.status === 200) {
        const data = response.data.data;
        setPresentStates(data);
        if (presentState) {
          const selectedState = data.find(state => state.state === presentState);
          setPresentCities(selectedState ? selectedState.cities : []);
        } else {
          setPresentCities([]);
        }
      }
    } catch (error) {
      console.error("Error fetching location data for present address:", error);
    }
  };

  const handlePresentCountryChange = e => {
    if (!hasAddressPermission) return;

    const selectedCountry = e.target.value;
    setPresentStates([]);
    setPresentCities([]);
    setInputs({
      ...inputs,
      presentCountry: selectedCountry,
      presentState: "",
      presentCity: ""
    });
    fetchPresentStatesAndCities(selectedCountry, " ");
    setIsEdited(true);
  };

  const handlePresentStateChange = e => {
    if (!hasAddressPermission) return;

    const selectedState = e.target.value;
    setInputs({
      ...inputs,
      presentState: selectedState,
      presentCity: ""
    });
    const stateData = presentStates.find(state => state.state === selectedState);
    setPresentCities(stateData ? stateData.cities : []);
    validateField("presentState", selectedState);
    validateField("presentCity", "");
    setIsEdited(true);
  };

  const handlePresentCityChange = e => {
    if (!hasAddressPermission) return;

    const selectedCity = e.target.value;
    setInputs({...inputs, presentCity: selectedCity});
    validateField("presentCity", selectedCity);
    setIsEdited(true);
  };

  const fetchPermanentStatesAndCities = async (country, permanentState) => {
    try {
      const url = `admin/location-list/${country}`;
      const response = await GET(url);
      if (response.status === 200) {
        const data = response.data.data;
        setPermanentStates(data);
        if (permanentState) {
          const selectedState = data.find(state => state.state === permanentState);
          setPermanentCities(selectedState ? selectedState.cities : []);
        } else {
          setPermanentCities([]);
        }
      }
    } catch (error) {
      console.error("Error fetching location data for permanent address:", error);
    }
  };

  const handlePermanentCountryChange = e => {
    if (!hasAddressPermission) return;

    const selectedCountry = e.target.value;
    setPermanentStates([]);
    setPermanentCities([]);
    setInputs({
      ...inputs,
      permanentCountry: selectedCountry,
      permanentState: "",
      permanentCity: ""
    });
    fetchPermanentStatesAndCities(selectedCountry, " ");
    setIsEdited(true);
  };

  const handlePermanentStateChange = e => {
    if (!hasAddressPermission) return;

    const selectedState = e.target.value;
    setInputs({
      ...inputs,
      permanentState: selectedState,
      permanentCity: ""
    });
    const stateData = permanentStates.find(state => state.state === selectedState);
    setPermanentCities(stateData ? stateData.cities : []);
    validateField("permanentState", selectedState);
    validateField("permanentCity", "");
    setIsEdited(true);
  };

  const handlePermanentCityChange = e => {
    if (!hasAddressPermission) return;

    const selectedCity = e.target.value;
    setInputs({
      ...inputs,
      permanentCity: selectedCity
    });
    validateField("permanentCity", selectedCity);
    setIsEdited(true);
  };

  return (
    <Container>
      <MDBox pt={2} pb={3} px={3} component="form">
        <MDBox mb={4} mr={3}>
          <InputLabel sx={{fontWeight: "bold", fontFamily: properties.fontFamily, mb: 1}}>
            Present Address<span style={{color: properties.colors.Red}}>*</span>
          </InputLabel>
          <MDInput
            sx={{m: 1, width: "30ch"}}
            type="text"
            name="presentAddressLine1"
            label={
              <span>
                Address Line 1<span style={{color: properties.colors.Red}}>*</span>
              </span>
            }
            variant="standard"
            value={inputs.presentAddressLine1 || ""}
            onChange={handleChange}
            error={!!errors.presentAddressLine1}
            helperText={errors.presentAddressLine1}
            disabled={!hasAddressPermission}
          />
          <MDInput
            sx={{m: 1, width: "30ch"}}
            type="text"
            name="presentAddressLine2"
            label={
              <span>
                Address Line 2<span style={{color: properties.colors.Red}}>*</span>
              </span>
            }
            variant="standard"
            value={inputs.presentAddressLine2 || ""}
            onChange={handleChange}
            error={!!errors.presentAddressLine2}
            helperText={errors.presentAddressLine2}
            disabled={!hasAddressPermission}
          />
          <MDInput
            sx={{m: 1, width: "15ch"}}
            type="text"
            name="presentAddressLine3"
            label="Landmark"
            variant="standard"
            value={inputs.presentAddressLine3 || ""}
            onChange={handleChange}
            // error={!!errors.presentAddressLine3}
            // helperText={errors.presentAddressLine3}
            disabled={!hasAddressPermission}
          />
        </MDBox>
        <MDBox mb={2}>
          <FormControl variant="standard" sx={{m: 1, width: "10ch"}}>
            <InputLabel>
              Country<span style={{color: properties.colors.Red}}>*</span>
            </InputLabel>
            <Select
              name="presentCountry"
              value={inputs.presentCountry}
              onChange={handlePresentCountryChange}
              label="Country Code"
              readOnly={!hasAddressPermission}
            >
              {presentCountries && presentCountries.length > 0 ? (
                presentCountries.map(country => (
                  <MenuItem key={country.countryId} value={country.countryCode}>
                    {country.countryName} ({country.countryCode})
                  </MenuItem>
                ))
              ) : (
                <MenuItem disabled>No countries available</MenuItem>
              )}
            </Select>
          </FormControl>
          <FormControl variant="standard" sx={{m: 1, width: "19ch"}}>
            <InputLabel>
              State<span style={{color: properties.colors.Red}}>*</span>
            </InputLabel>
            <Select
              name="presentState"
              value={inputs.presentState || ""}
              label="State"
              onChange={handlePresentStateChange}
              readOnly={!hasAddressPermission}
            >
              <MenuItem value="">
                <em>Select</em>
              </MenuItem>
              {presentStates.map((state, index) => (
                <MenuItem key={index} value={state.state}>
                  {state.state}
                </MenuItem>
              ))}
            </Select>
            {errors.presentState && <FormHelperText error>{errors.presentState}</FormHelperText>}
          </FormControl>
          <FormControl variant="standard" sx={{m: 1, width: "30ch"}}>
            <InputLabel>
              City<span style={{color: properties.colors.Red}}>*</span>
            </InputLabel>
            <Select
              name="presentCity"
              value={inputs.presentCity || ""}
              label="City *"
              onChange={handlePresentCityChange}
              readOnly={!hasAddressPermission || !inputs.presentState}
            >
              <MenuItem value="">
                <em>Select</em>
              </MenuItem>
              {presentCities.map((city, index) => (
                <MenuItem key={index} value={city}>
                  {city}
                </MenuItem>
              ))}
            </Select>
            {errors.presentCity && <FormHelperText error>{errors.presentCity}</FormHelperText>}
          </FormControl>

          <MDInput
            sx={{width: "15ch"}}
            type="text"
            name="presentPincode"
            label={
              <span>
                Pincode<span style={{color: properties.colors.Red}}>*</span>
              </span>
            }
            variant="standard"
            value={inputs.presentPincode || ""}
            onChange={handleChange}
            error={!!errors.presentPincode}
            helperText={errors.presentPincode}
            disabled={!hasAddressPermission}
          />
        </MDBox>

        <MDBox mb={2}>
          <FormControlLabel
            control={
              <Checkbox
                checked={isSameAddress}
                onChange={handleCheckboxChange}
                name="isSameAddress"
                color="primary"
                disabled={!hasAddressPermission}
              />
            }
            label="Same as Present Address"
          />
        </MDBox>

        <MDBox mb={4} mr={3}>
          <InputLabel
            sx={{fontWeight: "bold", fontFamily: properties.fontFamily, mb: 1}}
            id="permanent"
          >
            Permanent Address<span style={{color: properties.colors.Red}}>*</span>
          </InputLabel>
          <MDInput
            sx={{m: 1, width: "30ch"}}
            type="text"
            label={
              <span>
                Address Line 1<span style={{color: properties.colors.Red}}>*</span>
              </span>
            }
            variant="standard"
            value={inputs.permanentAddressLine1 || ""}
            name="permanentAddressLine1"
            onChange={handleChange}
            disabled={isSameAddress || !hasAddressPermission}
            error={!!errors.permanentAddressLine1}
            helperText={errors.permanentAddressLine1}
          />
          <MDInput
            sx={{m: 1, width: "30ch"}}
            type="text"
            label={
              <span>
                Address Line 2<span style={{color: properties.colors.Red}}>*</span>
              </span>
            }
            variant="standard"
            value={inputs.permanentAddressLine2 || ""}
            name="permanentAddressLine2"
            onChange={handleChange}
            disabled={isSameAddress || !hasAddressPermission}
            error={!!errors.permanentAddressLine2}
            helperText={errors.permanentAddressLine2}
          />
          <MDInput
            sx={{m: 1, width: "15ch"}}
            type="text"
            label="Landmark"
            variant="standard"
            value={inputs.permanentAddressLine3 || ""}
            name="permanentAddressLine3"
            onChange={handleChange}
            disabled={isSameAddress || !hasAddressPermission}
            // error={!!errors.permanentAddressLine3}
            // helperText={errors.permanentAddressLine3}
          />
        </MDBox>
        <MDBox mb={2}>
          <FormControl variant="standard" sx={{m: 1, width: "10ch"}}>
            <InputLabel>
              Country<span style={{color: properties.colors.Red}}>*</span>
            </InputLabel>
            <Select
              name="countryCode"
              value={inputs.permanentCountry}
              onChange={handlePermanentCountryChange}
              label="Country Code"
              readOnly={isSameAddress || !hasAddressPermission}
            >
              {permanentCountries && permanentCountries.length > 0 ? (
                permanentCountries.map(country => (
                  <MenuItem key={country.countryId} value={country.countryCode}>
                    {country.countryName} ({country.countryCode})
                  </MenuItem>
                ))
              ) : (
                <MenuItem disabled>No countries available</MenuItem>
              )}
            </Select>
          </FormControl>
          <FormControl variant="standard" sx={{m: 1, width: "19ch"}}>
            <InputLabel id="State2">
              State<span style={{color: properties.colors.Red}}>*</span>
            </InputLabel>
            <Select
              id="demo-simple-select-standard"
              value={inputs.permanentState || ""}
              label="state"
              name="permanentState"
              onChange={handlePermanentStateChange}
              readOnly={isSameAddress || !hasAddressPermission}
            >
              <MenuItem value="">
                <em>Select</em>
              </MenuItem>
              {permanentStates.map((state, index) => (
                <MenuItem key={index} value={state.state}>
                  {state.state}
                </MenuItem>
              ))}
            </Select>
            {errors.permanentState && (
              <FormHelperText error>{errors.permanentState}</FormHelperText>
            )}
          </FormControl>
          <FormControl variant="standard" sx={{m: 1, width: "30ch"}}>
            <InputLabel id="city2">
              City<span style={{color: properties.colors.Red}}>*</span>
            </InputLabel>
            <Select
              labelId="demo-simple-select-standard-label"
              id="demo-simple-select-standard"
              value={inputs.permanentCity || ""}
              name="permanentCity"
              label="City *"
              onChange={handlePermanentCityChange}
              readOnly={isSameAddress || !hasAddressPermission}
            >
              <MenuItem value="">
                <em>Select</em>
              </MenuItem>
              {permanentCities.map((city, index) => (
                <MenuItem key={index} value={city}>
                  {city}
                </MenuItem>
              ))}
            </Select>
            {errors.permanentCity && <FormHelperText error>{errors.permanentCity}</FormHelperText>}
          </FormControl>

          <MDInput
            sx={{width: "15ch"}}
            type="text"
            label={
              <span>
                Pincode<span style={{color: properties.colors.Red}}>*</span>
              </span>
            }
            variant="standard"
            name="permanentPincode"
            value={inputs.permanentPincode || ""}
            onChange={handleChange}
            disabled={isSameAddress || !hasAddressPermission}
            error={!!errors.permanentPincode}
            helperText={errors.permanentPincode}
          />
        </MDBox>
        <Divider />
        <MDBox
          mt={2}
          sx={{display: "flex", justifyContent: "flex-end", gridAutoColumns: "1fr", gap: 1}}
        >
          <MDButton
            variant="gradient"
            color="info"
            sx={{width: "30ch", background: properties.colors.bgcolorbluethem}}
            onClick={handleSubmit}
            disabled={!hasAddressPermission || !isEdited}
          >
            Save
          </MDButton>
        </MDBox>
        <Snackbar
          open={open}
          anchorOrigin={{vertical: "top", horizontal: "right"}}
          autoHideDuration={6000}
          onClose={handleClose}
        >
          <Alert
            onClose={handleClose}
            severity={snakColor === "success" ? "success" : "error"}
            variant="filled"
            sx={{width: "100%", color: "#fff"}}
          >
            {snakMessage}
          </Alert>
        </Snackbar>
      </MDBox>
    </Container>
  );
}

export default EmployeeAddress;
